/* =========================================================================
   Progression tiers — staged onboarding disclosure (classic progressive
   unlock: keep a brand-new player's home to ONE clear next action, then open
   the deeper surfaces once they've shown they're diving in).

   Tier 0 = the new-player home (hero + Start + rules + Theo greeting only).
   Tier 1 = "diving deep": quest log, pilgrimage, feast sub-line, Collections
            & Monastery, full journey stops.

   The gate is a lifetime answered-question counter (localStorage
   'catholics.lifetimeQuestions', bumped off the existing QuestionAnswered beat)
   OR an authed player with real server history (existing accounts are never
   re-gated). The pure logic (tierFor / reducer / threshold-crossing / stats
   read) is unit-tested; storage rides the injectable KVStore seam dailyBlessing
   introduced. No new telemetry observer — main.ts bumps the counter from the
   single quests onQuestEvent fan-out.
   ========================================================================= */
import type { KVStore } from './dailyBlessing';
import { getGamesCompleted } from './easyStart';

/** Lifetime answered-question count that opens "deep mode" (Tier 1). */
export const DEEP_MODE_THRESHOLD = 30;

const LIFETIME_KEY = 'catholics.lifetimeQuestions';
const PENDING_KEY = 'catholics.deepModePending';
const SEEN_KEY = 'catholics.deepModeSeen';
const THEO_INTRO_KEY = 'catholics.theoIntroduced';

/* ------------------------------- pure logic ------------------------------- */

/** Advance the lifetime counter by delta (clamped: never below the current
    value, integer-only). PURE — the counter reducer. */
export function nextLifetime(current: number, delta = 1): number {
  const base = Number.isFinite(current) && current > 0 ? Math.floor(current) : 0;
  return base + Math.max(0, Math.floor(delta));
}

/** True when a bump from `before` to `after` first reaches the threshold (the
    single moment deep mode unlocks). PURE. */
export function crossedThreshold(before: number, after: number): boolean {
  return before < DEEP_MODE_THRESHOLD && after >= DEEP_MODE_THRESHOLD;
}

/** The disclosure tier: Tier 1 when the player has answered enough questions OR
    is an authed player with real server history; Tier 0 otherwise. PURE. */
export function tierFor(lifetimeQuestions: number, authedWithHistory: boolean): 0 | 1 {
  if (authedWithHistory) return 1;
  return lifetimeQuestions >= DEEP_MODE_THRESHOLD ? 1 : 0;
}

/** Whether a signed-in player's server stats show genuine progress — used so an
    existing account is never re-gated to the new-player home. PURE. */
export function authedWithHistoryFromStats(
  stats: { totalSessions?: number; xp?: number } | null | undefined,
): boolean {
  if (!stats) return false;
  return (stats.totalSessions || 0) > 0 || (stats.xp || 0) > 0;
}

/* ------------------------------- storage seam ------------------------------- */

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readLifetime(store: KVStore | null = deviceStore()): number {
  try {
    const raw = store?.getItem(LIFETIME_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeLifetime(n: number, store: KVStore | null): void {
  try {
    store?.setItem(LIFETIME_KEY, String(n));
  } catch {
    /* private mode — the counter simply won't persist */
  }
}

/** Record one (or `delta`) answered question(s). Returns the new lifetime total
    and whether THIS call crossed the deep-mode threshold (fire-the-unlock cue). */
export function recordQuestionAnswered(
  delta = 1,
  store: KVStore | null = deviceStore(),
): { total: number; crossed: boolean } {
  const before = readLifetime(store);
  const after = nextLifetime(before, delta);
  if (after !== before) writeLifetime(after, store);
  return { total: after, crossed: crossedThreshold(before, after) };
}

/* ---- pending / seen flags for the one-time "The road opens" unlock moment ---- */

export function isDeepModePending(store: KVStore | null = deviceStore()): boolean {
  try {
    return store?.getItem(PENDING_KEY) === '1';
  } catch {
    return false;
  }
}
export function setDeepModePending(on: boolean, store: KVStore | null = deviceStore()): void {
  try {
    store?.setItem(PENDING_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}
export function hasSeenDeepModeUnlock(store: KVStore | null = deviceStore()): boolean {
  try {
    return store?.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}
export function markDeepModeUnlockSeen(store: KVStore | null = deviceStore()): void {
  try {
    store?.setItem(SEEN_KEY, '1');
  } catch {
    /* ignore */
  }
}

/* ---- staged character introduction (one stage before deep mode) ----
   Characters earn their entrance DURING play: a brand-new player's home has no
   Theo card/candle/name-ask until Brother Theo introduces himself at the first
   break of the first game. Existing players are seeded TRUE at boot — nobody
   who has already met him is re-introduced. */

/** PURE: should an existing player skip the staged introduction entirely?
    True when they've completed a game, are authed with server history, or have
    any lifetime questions on this device. */
export function shouldSeedTheoIntroduced(
  gamesCompleted: number,
  authedWithHistory: boolean,
  lifetimeQuestions: number,
): boolean {
  return gamesCompleted > 0 || authedWithHistory || lifetimeQuestions > 0;
}

export function isTheoIntroduced(store: KVStore | null = deviceStore()): boolean {
  try {
    return store?.getItem(THEO_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}

export function markTheoIntroduced(store: KVStore | null = deviceStore()): void {
  try {
    store?.setItem(THEO_INTRO_KEY, '1');
  } catch {
    /* private mode — the intro may replay next session; harmless */
  }
}

/** Boot/auth-change migration: seed the introduced flag for anyone who has
    already met Theo (existing device play or an authed player with history). */
export function migrateTheoIntroduced(store: KVStore | null = deviceStore()): void {
  // Grandfather long-time GUESTS into deep mode: a completed game = 46 questions
  // answered before the lifetime counter existed, so seed it past the threshold
  // (authed players are already covered by the authed-with-history tier override).
  if (getGamesCompleted() > 0 && readLifetime(store) === 0) {
    writeLifetime(DEEP_MODE_THRESHOLD, store);
  }
  if (isTheoIntroduced(store)) return;
  if (shouldSeedTheoIntroduced(getGamesCompleted(), cachedAuthedHistory, readLifetime(store))) {
    markTheoIntroduced(store);
  }
}

/* ---- cached authed-history flag (set once from getProfileMe at boot) ---- */

let cachedAuthedHistory = false;

/** Cache whether the signed-in player has server history (set from getProfileMe).
    Guests / signed-out reset this to false. */
export function setAuthedHistory(v: boolean): void {
  cachedAuthedHistory = v;
}
export function getAuthedHistory(): boolean {
  return cachedAuthedHistory;
}

/** The live disclosure tier for this device/session right now. */
export function currentTier(store: KVStore | null = deviceStore()): 0 | 1 {
  return tierFor(readLifetime(store), cachedAuthedHistory);
}
